<script setup lang="ts">
import { ActionButton } from '@taler/ui'
import type { ManagedUser, UserSession } from '../internal/types.ts'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SessionsList from '@/features/identity-admin/components/SessionsList.vue'
import SuspensionPanel from '@/features/identity-admin/components/SuspensionPanel.vue'

const props = defineProps<{
  subject: string | undefined
  ownSubject: string | undefined
  sessions: readonly UserSession[]
  isLoadingSessions: boolean
  sessionsError: Error | null
  sessionsRetrying?: boolean
  endingSessionId: string | undefined
  endSessionError: Error | null
  status: ManagedUser | undefined
  isSuspending: boolean
  suspendError: Error | null
  isRestoring: boolean
  restoreError: Error | null
  locale: 'en' | 'hu'
}>()

const emit = defineEmits<{
  lookUp: [subject: string]
  endSession: [sessionId: string]
  retrySessions: []
  suspend: []
  restore: []
}>()

const { t } = useI18n()
const input = ref(props.subject ?? '')

function submit(): void {
  const trimmed = input.value.trim()
  if (trimmed !== '') emit('lookUp', trimmed)
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-section)]">
    <form class="flex items-end gap-[var(--space-group)]" @submit.prevent="submit">
      <div class="flex flex-1 flex-col gap-[var(--space-tight)]">
        <label
          for="identity-admin-subject"
          class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
        >
          {{ t('identityAdmin.subjectLabel') }}
        </label>
        <input
          id="identity-admin-subject"
          v-model="input"
          type="text"
          required
          :placeholder="t('identityAdmin.subjectPlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
        />
      </div>
      <ActionButton type="submit" class="shrink-0 text-[length:var(--text-step-3)]">
        {{ t('identityAdmin.lookUp') }}
      </ActionButton>
    </form>
    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('identityAdmin.subjectHelp') }}
    </p>

    <template v-if="props.subject !== undefined">
      <SuspensionPanel
        :subject="props.subject"
        :is-own-account="props.subject === props.ownSubject"
        :status="props.status"
        :is-suspending="props.isSuspending"
        :suspend-error="props.suspendError"
        :is-restoring="props.isRestoring"
        :restore-error="props.restoreError"
        @suspend="emit('suspend')"
        @restore="emit('restore')"
      />

      <SessionsList
        :sessions="props.sessions"
        :is-loading="props.isLoadingSessions"
        :error="props.sessionsError"
        :ending-session-id="props.endingSessionId"
        :end-session-error="props.endSessionError"
        :locale="props.locale"
        @end="emit('endSession', $event)"
        :retrying="props.sessionsRetrying"
        @retry="emit('retrySessions')"
      />
    </template>
  </div>
</template>
